import { useQuery } from "@apollo/client";
import { Button, Intent, MenuItem } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { ItemRenderer, MultiSelect } from "@blueprintjs/select";
import useMapViewConfiguration from "contexts/MapViewConfigurationContext/hooks/useMapViewConfiguration";
import { useMapViewRoutingMetadata } from "contexts/RoutingMetadataContext";
import { useScreeningViewContext } from "contexts/ScreeningViewContext/ScreeningViewContext";
import {
    GetOwnersByRegionAndScopeQuery,
    GetOwnersByRegionAndScopeQueryVariables
} from "generated/graphql";
import { loader } from "graphql.macro";
import React, { useMemo } from "react";

const GET_OWNERS_BY_REGION_AND_SCOPE = loader(
    "src/graphql/getOwnersByRegionAndScope.graphql"
);

const SELECT_ALL = "SELECT_ALL";

const OwnersFilter: React.FC = () => {
    const {
        busFiltersConfiguration: { busFilters, setOwners }
    } = useMapViewConfiguration();
    const { owners } = busFilters;
    const { region, scopeView } = useMapViewRoutingMetadata();
    const maybeScreeningViewData = useScreeningViewContext();

    const { data, loading } = useQuery<
        GetOwnersByRegionAndScopeQuery,
        GetOwnersByRegionAndScopeQueryVariables
    >(GET_OWNERS_BY_REGION_AND_SCOPE, {
        variables: {
            region,
            scope: busFilters.scope,
            scopeView
        },
        fetchPolicy: "cache-first"
    });

    // Get unique list of owners from the entire region/scope
    const availableOwners = useMemo(() => {
        if (!data?.buses) return [];
        return data.buses
            .map((bus) => bus.owner_name)
            .filter((name): name is string => name !== null && name !== "")
            .sort();
    }, [data]);

    // When in a screening view, only show owners that were saved with the view
    const screeningViewOwners = maybeScreeningViewData?.owners;
    const hasScreeningViewOwnerFilter =
        screeningViewOwners && screeningViewOwners.length > 0;

    // null means all owners selected, [] means none selected
    const selectedOwners = owners ?? [];
    const allSelected =
        owners === null ||
        (availableOwners.length > 0 &&
            availableOwners.every((o) => selectedOwners.includes(o)));
    const noneSelected = owners !== null && owners.length === 0;

    const ownerRenderer: ItemRenderer<string> = (
        item,
        { handleClick, modifiers }
    ) => {
        if (!modifiers.matchesPredicate) {
            return null;
        }

        if (item === SELECT_ALL) {
            return (
                <MenuItem
                    active={modifiers.active}
                    key={SELECT_ALL}
                    text="Select All"
                    onClick={handleClick}
                    role="listoption"
                    icon={allSelected ? IconNames.SMALL_TICK : IconNames.BLANK}
                />
            );
        }

        return (
            <MenuItem
                active={modifiers.active}
                key={item}
                text={item}
                onClick={handleClick}
                role="listoption"
                icon={
                    allSelected || selectedOwners.includes(item)
                        ? IconNames.SMALL_TICK
                        : IconNames.BLANK
                }
                disabled={
                    hasScreeningViewOwnerFilter &&
                    !screeningViewOwners.includes(item)
                }
            />
        );
    };

    const handleItemSelect = (item: string) => {
        if (item === SELECT_ALL) {
            setOwners(allSelected ? [] : null);
            return;
        }

        if (allSelected) {
            // Switching from "all" to specific selection - select all except this one
            setOwners(availableOwners.filter((o) => o !== item));
        } else if (selectedOwners.includes(item)) {
            setOwners(selectedOwners.filter((o) => o !== item));
        } else {
            setOwners(Array.from(new Set([...selectedOwners, item])));
        }
    };

    const showSummaryTag =
        !allSelected && !noneSelected && selectedOwners.length >= 5;

    const getPlaceholder = () => {
        if (loading) return "Loading...";
        if (allSelected) return "All owners";
        if (noneSelected) return "No owners";
        return "";
    };

    const clearOwners = () => setOwners([]);

    const clearButton =
        !noneSelected && !loading ? (
            <Button
                icon={IconNames.CROSS}
                minimal={true}
                small={true}
                onClick={clearOwners}
            />
        ) : undefined;

    return (
        <MultiSelect
            className="OwnersFilter-select"
            popoverProps={{ minimal: true }}
            items={[SELECT_ALL, ...availableOwners]}
            selectedItems={
                allSelected || noneSelected
                    ? []
                    : showSummaryTag
                    ? [selectedOwners[0]]
                    : [...selectedOwners]
            }
            fill={true}
            tagRenderer={(owner) =>
                showSummaryTag ? `${selectedOwners.length} owners` : owner
            }
            itemRenderer={ownerRenderer}
            onItemSelect={handleItemSelect}
            tagInputProps={{
                tagProps: { minimal: true, intent: Intent.PRIMARY },
                onRemove: showSummaryTag ? clearOwners : undefined,
                rightElement: clearButton
            }}
            onRemove={
                showSummaryTag
                    ? undefined
                    : (item) =>
                          setOwners(selectedOwners.filter((o) => o !== item))
            }
            placeholder={getPlaceholder()}
            disabled={loading}
        />
    );
};

export default OwnersFilter;
